import React, { useState } from "react";
import PayerStrip from "../components/PayerStrip";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ShieldCheck, HeartHandshake, ClipboardCheck } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { leadsApi, track } from "../api";
import useSeo from "../hooks/useSeo";

const PROGRAM_OPTIONS = [
  "Anthem", "CareSource", "Molina", "Buckeye / Wellcare",
  "Next Generation MyCare", "PASSPORT", "Ohio DODD", "Private Pay", "Other",
];

const EMPTY = {
  name: "", phone: "", email: "", county: "",
  program: "", memberPlan: "", service: "", startDate: "",
};

const MyCare = () => {
  useSeo({
    title: "Next Generation MyCare Ohio & Coverage Verification | Caregivers Alliance Homecare",
    description: "Verify your home care coverage: Next Generation MyCare Ohio, PASSPORT, Ohio DODD, Anthem, CareSource, Molina, Buckeye/Wellcare & private pay. Home care across Ohio. Call (614) 526-1548.",
    keywords: "Next Generation MyCare Ohio home care, verify home care coverage Ohio, PASSPORT waiver provider Ohio, DODD provider Columbus, MyCare Ohio home care",
    path: "/mycare",
  });
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const parts = [
      `Program: ${form.program || "—"}`,
      form.memberPlan && `Member plan: ${form.memberPlan}`,
      form.county && `County: ${form.county}`,
      form.service && `Requested service: ${form.service}`,
      form.startDate && `Desired start: ${form.startDate}`,
    ].filter(Boolean).join(" • ");
    try {
      await leadsApi.create({
        name: form.name,
        contact: form.phone || form.email,
        email: form.email,
        phone: form.phone,
        source: "Website",
        org: "—",
        note: `Coverage verification request — ${parts}`,
      });
      toast({ title: "Request received!", description: "We'll verify your coverage and call you back shortly." });
      track("coverage_verification", form.county);
      setForm(EMPTY);
    } catch {
      toast({ title: "Something went wrong", description: "Please call us at (614) 526-1548.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <h1 className="font-display font-extrabold text-4xl md:text-5xl">Next Generation MyCare Ohio</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            Coverage verification for MyCare Ohio, PASSPORT, Ohio DODD and managed-care plans.
            Tell us about your plan and we'll confirm eligibility before care begins.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "MyCare Ohio", text: "Next Generation MyCare coordinates Medicare and Medicaid benefits through Ohio managed-care plans, which can include in-home personal care and homemaker support." },
            { icon: HeartHandshake, title: "PASSPORT Waiver", text: "PASSPORT is an Ohio Medicaid home and community-based services waiver administered through the Ohio Department of Aging and regional Area Agencies on Aging." },
            { icon: ClipboardCheck, title: "We Verify For You", text: "Every plan is different. Share your information below and our intake team will confirm eligibility, authorization and participation before services begin." },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-7">
                <div className="brand-gradient h-11 w-11 rounded-xl grid place-items-center text-white mb-4">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display font-bold text-lg text-[#0E2A33]">{c.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <PayerStrip ctaHref="#verify-form" />

      <section id="verify-form" className="section bg-white scroll-mt-24">
        <div className="mx-auto max-w-3xl px-4">
          <div className="text-center">
            <span className="text-[#2E9E6A] font-semibold uppercase tracking-wide text-sm">Verify My Coverage</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-[#0E2A33] mt-2">
              Coverage verification request
            </h2>
            <p className="text-slate-600 mt-4">
              We only ask for what's needed to verify your plan — no medical details required.
            </p>
          </div>

          <form onSubmit={submit} data-testid="coverage-form" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-4 mt-8">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="coverage-name-input" required value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-1" placeholder="Full name" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="coverage-phone-input" required value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="mt-1" placeholder="(614) 555-0100" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input data-testid="coverage-email-input" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="mt-1" placeholder="you@email.com" />
              </div>
              <div>
                <Label>County</Label>
                <Input data-testid="coverage-county-input" value={form.county} onChange={(e) => set("county")(e.target.value)} className="mt-1" placeholder="e.g. Franklin" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Insurance / Program</Label>
                <Select value={form.program} onValueChange={set("program")}>
                  <SelectTrigger data-testid="coverage-program-select" className="mt-1"><SelectValue placeholder="Select plan or program" /></SelectTrigger>
                  <SelectContent>
                    {PROGRAM_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Member Plan (if known)</Label>
                <Input data-testid="coverage-memberplan-input" value={form.memberPlan} onChange={(e) => set("memberPlan")(e.target.value)} className="mt-1" placeholder="Plan name on your card" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Requested Service</Label>
                <Input data-testid="coverage-service-input" value={form.service} onChange={(e) => set("service")(e.target.value)} className="mt-1" placeholder="Personal care, homemaker, respite..." />
              </div>
              <div>
                <Label>Desired Start Date</Label>
                <Input data-testid="coverage-startdate-input" type="date" value={form.startDate} onChange={(e) => set("startDate")(e.target.value)} className="mt-1" />
              </div>
            </div>
            <Button data-testid="coverage-submit-btn" type="submit" disabled={busy} className="w-full bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-12">
              {busy ? "Sending..." : "Verify My Coverage"}
            </Button>
            <p className="text-xs text-slate-400 text-center">
              Contact us to verify current eligibility and participation. Please do not include medical or diagnosis information in this form.
            </p>
          </form>
        </div>
      </section>
    </>
  );
};

export default MyCare;
